import type { Answer } from '../types/answers'
import type { Quest } from '../types/quests'

type ChildQuestLike = Omit<Partial<Quest>, 'id'> & {
    id?: string | null
}

type AnswerLike = Omit<Partial<Answer>, 'id' | 'quest_id' | 'child_quests'> & {
    id?: string | null
    quest_id?: string | null
    uptree_quest_id?: string | null
    downtree_quest_id?: string | null
    child_quests?: ChildQuestLike[]
}

type QuestLike = Omit<
    Partial<Quest>,
    | 'id'
    | 'parent'
    | 'parent_id'
    | 'grand_parent'
    | 'grand_parent_id'
    | 'sorted_answers'
    | 'sorted_answer_ids'
> & {
    id?: string | null
    parent?: AnswerLike | null
    parent_id?: string | null
    parent_quest_id?: string | null
    grand_parent?: AnswerLike | null
    grand_parent_id?: string | null
    sorted_answers?: AnswerLike[]
    sorted_answer_ids?: Array<string | number>
}

type AnswerLookup = (answerId: string) => AnswerLike | null | undefined

export type ParentQuestFetchState = 'loading' | 'available' | 'private' | 'missing' | 'unavailable'

export type QuestWithAncestors = QuestLike

/**
 * Shared quest ancestor normalization and thin-preview state helpers.
 *
 * These functions stay pure on purpose so cache hydration, API hydration,
 * UI rendering, and unit tests all use the same ancestor rules.
 */

export function getAnswerQuestIds(answer: AnswerLike | null | undefined): {
    uptreeQuestId: string | null
    downtreeQuestId: string | null
} {
    const childQuestId =
        answer?.child_quests?.[0]?.id != null ? String(answer.child_quests[0].id) : null
    const rawUptreeQuestId =
        answer?.quest_id != null
            ? String(answer.quest_id)
            : answer?.uptree_quest_id != null
            ? String(answer.uptree_quest_id)
            : null

    const downtreeQuestId =
        answer?.downtree_quest_id != null
            ? String(answer.downtree_quest_id)
            : Array.isArray(answer?.child_quests) && answer.child_quests[0]?.id != null
            ? childQuestId
            : null

    const isRootTitleAnswer =
        answer?.quest_id == null &&
        answer?.downtree_quest_id == null &&
        !!childQuestId &&
        rawUptreeQuestId === childQuestId

    const uptreeQuestId = isRootTitleAnswer ? null : rawUptreeQuestId

    return {
        uptreeQuestId,
        downtreeQuestId,
    }
}

export function getEmbeddedAnswerForId(
    answer: AnswerLike | null | undefined,
    expectedId: string | null,
): AnswerLike | null {
    if (!answer || !expectedId) return null
    return String(answer.id) === expectedId ? answer : null
}

export function getEmbeddedSortedAnswersById(
    quest: QuestWithAncestors | null | undefined,
): Map<string, AnswerLike> {
    return new Map(
        (Array.isArray(quest?.sorted_answers) ? quest.sorted_answers : [])
            .filter(answer => answer?.id != null)
            .map(answer => [String(answer.id), answer]),
    )
}

function answerPlaceholder(answerId: string, index: number): AnswerLike {
    return {
        id: answerId,
        position: index,
        created_at: new Date(0) as unknown as AnswerLike['created_at'],
    }
}

export function normalizeQuestAncestors({
    quest,
    fallbackQuest = null,
    getAnswerById = () => null,
    hydrateSortedAnswers = true,
}: {
    quest: QuestWithAncestors | null | undefined
    fallbackQuest?: QuestWithAncestors | null | undefined
    getAnswerById?: AnswerLookup
    hydrateSortedAnswers?: boolean
}): QuestWithAncestors | null {
    if (!quest) return null

    const normalizedQuest: QuestWithAncestors = { ...quest }
    const fallbackSortedAnswersById = getEmbeddedSortedAnswersById(fallbackQuest)
    const incomingSortedAnswersById = getEmbeddedSortedAnswersById(normalizedQuest)

    const parentId = normalizedQuest.parent_id ? String(normalizedQuest.parent_id) : null
    const incomingParent = getEmbeddedAnswerForId(normalizedQuest.parent, parentId)
    const fallbackParent = getEmbeddedAnswerForId(fallbackQuest?.parent, parentId)
    const resolvedParent =
        (parentId ? getAnswerById(parentId) : null) || incomingParent || fallbackParent || null

    const grandParentId = normalizedQuest.grand_parent_id
        ? String(normalizedQuest.grand_parent_id)
        : null
    const incomingGrandParent = getEmbeddedAnswerForId(normalizedQuest.grand_parent, grandParentId)
    const fallbackGrandParent = getEmbeddedAnswerForId(fallbackQuest?.grand_parent, grandParentId)
    const resolvedGrandParent =
        (grandParentId ? getAnswerById(grandParentId) : null) ||
        incomingGrandParent ||
        fallbackGrandParent ||
        null

    if (Array.isArray(normalizedQuest.sorted_answer_ids)) {
        const sortedAnswerIds =
            hydrateSortedAnswers &&
            normalizedQuest.__knovAnswerHydrationScope === 'stream-preview' &&
            Array.isArray(normalizedQuest.__knovStreamPreviewAnswerIds)
                ? normalizedQuest.__knovStreamPreviewAnswerIds
                : normalizedQuest.sorted_answer_ids
        normalizedQuest.sorted_answers = sortedAnswerIds.map((answerId, index) => {
            const normalizedId = String(answerId)
            if (hydrateSortedAnswers) {
                const resolvedAnswer = getAnswerById(normalizedId)
                if (resolvedAnswer) return resolvedAnswer
            }

            const incomingAnswer = incomingSortedAnswersById.get(normalizedId)
            if (incomingAnswer) return incomingAnswer

            return hydrateSortedAnswers
                ? fallbackSortedAnswersById.get(normalizedId) ||
                      answerPlaceholder(normalizedId, index)
                : answerPlaceholder(normalizedId, index)
        }) as AnswerLike[]
    }

    normalizedQuest.parent = parentId ? resolvedParent || answerPlaceholder(parentId, 0) : null
    normalizedQuest.grand_parent = grandParentId ? resolvedGrandParent : null
    normalizedQuest.parent_quest_id = resolveQuestParentQuestId({
        questId: normalizedQuest.id != null ? String(normalizedQuest.id) : null,
        parentQuestId: normalizedQuest.parent_quest_id,
        parentId,
        parentAnswer: normalizedQuest.parent,
        fallbackQuest,
        getAnswerById,
    })

    return normalizedQuest
}

// A thread is never its own parent. A head post can still name its own thread:
// a share's post starts out inside the thread before it becomes the head, and
// a copy kept from that moment says so. Pass questId so that copy resolves to
// no parent (a head post names its own thread only on a root).
export function resolveQuestParentQuestId({
    questId = null,
    ...rest
}: {
    questId?: string | null
    parentQuestId?: string | null
    parentId?: string | null
    parentAnswer?: AnswerLike | null
    fallbackQuest?: QuestWithAncestors | null | undefined
    getAnswerById?: AnswerLookup
}): string | null {
    const parentQuestId = findQuestParentQuestId(rest)
    return questId != null && parentQuestId === String(questId) ? null : parentQuestId
}

function findQuestParentQuestId({
    parentQuestId = null,
    parentId = null,
    parentAnswer = null,
    fallbackQuest = null,
    getAnswerById = () => null,
}: {
    parentQuestId?: string | null
    parentId?: string | null
    parentAnswer?: AnswerLike | null
    fallbackQuest?: QuestWithAncestors | null | undefined
    getAnswerById?: AnswerLookup
}): string | null {
    if (parentQuestId) return String(parentQuestId)
    const parentAnswerQuestId = getAnswerQuestIds(parentAnswer).uptreeQuestId
    if (parentAnswerQuestId) return parentAnswerQuestId

    const normalizedParentId = parentId ? String(parentId) : null
    const cachedParentAnswer = normalizedParentId ? getAnswerById(normalizedParentId) : null
    const cachedParentQuestId = getAnswerQuestIds(cachedParentAnswer).uptreeQuestId
    if (cachedParentQuestId) return cachedParentQuestId

    if (fallbackQuest?.parent_quest_id) return String(fallbackQuest.parent_quest_id)
    const fallbackParentQuestId = getAnswerQuestIds(fallbackQuest?.parent).uptreeQuestId
    if (fallbackParentQuestId) return fallbackParentQuestId

    return null
}

export function getThinParentQuestState({
    quest = null,
    getAnswerById = () => null,
}: {
    quest?: QuestWithAncestors | null
    getAnswerById?: AnswerLookup
}): {
    quest: QuestWithAncestors | null
    parentId: string | null
    parentQuestId: string | null
    grandParent: AnswerLike | null
    grandParentId: string | null
    grandParentQuest: ChildQuestLike | null
    hasParentPreview: boolean
} {
    const normalizedQuest = normalizeQuestAncestors({ quest, getAnswerById })
    const normalizedQuestId = normalizedQuest?.id != null ? String(normalizedQuest.id) : null
    const parentId =
        normalizedQuest?.parent_id != null
            ? String(normalizedQuest.parent_id)
            : normalizedQuest?.parent?.id != null
            ? String(normalizedQuest.parent.id)
            : null
    const parentQuestId = resolveQuestParentQuestId({
        parentQuestId: normalizedQuest?.parent_quest_id,
        parentId,
        parentAnswer: normalizedQuest?.parent || null,
        getAnswerById,
    })
    const grandParent =
        normalizedQuest?.grand_parent?.id != null ? normalizedQuest.grand_parent : null
    const grandParentQuest = Array.isArray(grandParent?.child_quests)
        ? grandParent.child_quests.find(
              childQuest =>
                  childQuest?.id && String(childQuest.id) !== String(normalizedQuest?.id || ''),
          ) ||
          grandParent.child_quests.find(childQuest => childQuest?.id) ||
          null
        : null
    const grandParentId =
        normalizedQuest?.grand_parent_id != null
            ? String(normalizedQuest.grand_parent_id)
            : grandParent?.id != null
            ? String(grandParent.id)
            : null
    const resolvedParentQuestId =
        parentQuestId || (grandParentQuest?.id ? String(grandParentQuest.id) : null)
    const effectiveParentQuestId =
        normalizedQuestId && resolvedParentQuestId === normalizedQuestId
            ? null
            : resolvedParentQuestId

    return {
        quest: normalizedQuest,
        parentId,
        parentQuestId: effectiveParentQuestId,
        grandParent,
        grandParentId,
        grandParentQuest,
        hasParentPreview: !!(
            parentId ||
            effectiveParentQuestId ||
            grandParentId ||
            grandParent?.id
        ),
    }
}

export function resolveThinParentState({
    quest = null,
    parentQuest = null,
    parentQuestFetchState = 'missing',
    getAnswerById = () => null,
}: {
    quest?: QuestWithAncestors | null
    parentQuest?: QuestWithAncestors | null
    parentQuestFetchState?: ParentQuestFetchState
    getAnswerById?: AnswerLookup
}): {
    parentQuestId: string | null
    displayGrandParent: AnswerLike | null
    displayGrandParentId: string | null
    canOpenParentAnswer: boolean
    isLockedPreview: boolean
    shouldRenderUnavailablePreview: boolean
    unavailablePreviewLabel: string
    unavailablePreviewSubtext: string
} {
    const normalizedQuest = quest || null
    const normalizedQuestId = normalizedQuest?.id != null ? String(normalizedQuest.id) : null
    const resolvedParentQuest = parentQuest?.id ? parentQuest : null
    const resolvedParentQuestId = resolveQuestParentQuestId({
        parentQuestId: normalizedQuest?.parent_quest_id,
        parentId: normalizedQuest?.parent_id,
        parentAnswer: normalizedQuest?.parent || null,
        getAnswerById,
    })
    const parentQuestId =
        normalizedQuestId && resolvedParentQuestId === normalizedQuestId
            ? null
            : resolvedParentQuestId
    const isSelfParentQuest =
        !!normalizedQuestId &&
        !!resolvedParentQuest?.id &&
        String(resolvedParentQuest.id) === normalizedQuestId

    const displayGrandParent =
        (normalizedQuest?.grand_parent?.id ? normalizedQuest.grand_parent : null) ||
        (normalizedQuest?.grand_parent_id
            ? getAnswerById(String(normalizedQuest.grand_parent_id))
            : null) ||
        (!isSelfParentQuest && resolvedParentQuest?.parent?.id
            ? resolvedParentQuest.parent
            : null) ||
        (!isSelfParentQuest && resolvedParentQuest?.parent_id
            ? getAnswerById(String(resolvedParentQuest.parent_id))
            : null) ||
        null

    const displayGrandParentId =
        displayGrandParent?.id ||
        normalizedQuest?.grand_parent_id ||
        (!isSelfParentQuest ? resolvedParentQuest?.parent_id : null) ||
        null

    const canOpenParentAnswer =
        !!parentQuestId &&
        !!normalizedQuest?.parent_id &&
        parentQuestFetchState !== 'private' &&
        parentQuestFetchState !== 'missing'
    const isLockedPreview = parentQuestFetchState === 'private'

    const shouldRenderUnavailablePreview =
        !!normalizedQuest?.parent_id &&
        !displayGrandParent?.id &&
        (parentQuestFetchState === 'loading' ||
            parentQuestFetchState === 'private' ||
            parentQuestFetchState === 'unavailable' ||
            !!parentQuestId ||
            !!displayGrandParentId)

    const unavailablePreviewLabel =
        parentQuestFetchState === 'loading'
            ? 'Loading parent thread'
            : parentQuestFetchState === 'private'
            ? 'Parent thread locked'
            : parentQuestFetchState === 'unavailable'
            ? 'Parent thread unavailable'
            : canOpenParentAnswer
            ? 'Open parent thread'
            : 'Parent thread unavailable'

    const unavailablePreviewSubtext =
        parentQuestFetchState === 'loading'
            ? 'Resolving ancestor preview'
            : parentQuestFetchState === 'private'
            ? 'You may not have access to this parent'
            : parentQuestFetchState === 'unavailable'
            ? 'Parent exists but its thread could not be resolved'
            : canOpenParentAnswer
            ? 'Preview unavailable'
            : 'Parent exists but preview could not load'

    return {
        parentQuestId,
        displayGrandParent,
        displayGrandParentId: displayGrandParentId ? String(displayGrandParentId) : null,
        canOpenParentAnswer,
        isLockedPreview,
        shouldRenderUnavailablePreview,
        unavailablePreviewLabel,
        unavailablePreviewSubtext,
    }
}
